import {
  BoxGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  type Texture,
} from 'three'
import { Rng } from '@/core/Rng'
import type { PropKind } from '@/components'

/**
 * Every obstacle in the game, built from boxes and cylinders.
 *
 * Each prop takes a seed so no two crates, trees or wrecks are identical —
 * repetition is what makes procedural scenery look cheap, and a few degrees of
 * rotation plus a few centimetres of scale kills most of it.
 */

export interface PropTextures {
  concrete: Texture
  rust: Texture
  bark: Texture
  roughness: Texture
}

const GEO = {
  crate: new BoxGeometry(1, 1, 1),
  plank: new BoxGeometry(1.04, 0.09, 0.09),
  barrel: new CylinderGeometry(0.44, 0.44, 1.02, 14),
  barrelRing: new CylinderGeometry(0.47, 0.47, 0.07, 14),
  trunk: new CylinderGeometry(0.24, 0.36, 3.4, 8),
  branch: new CylinderGeometry(0.05, 0.11, 1.5, 6),
  pillar: new CylinderGeometry(0.16, 0.22, 4.4, 8),
  pillarArm: new BoxGeometry(1.5, 0.12, 0.12),
  lamp: new ConeGeometry(0.22, 0.32, 8),
  barrier: new BoxGeometry(1.5, 0.62, 0.16),
  barrierLeg: new BoxGeometry(0.1, 0.5, 0.1),
  block: new BoxGeometry(3.1, 1.5, 3.1),
  carBody: new BoxGeometry(4.5, 0.85, 1.95),
  carCabin: new BoxGeometry(2.1, 0.72, 1.75),
  wheel: new CylinderGeometry(0.38, 0.38, 0.28, 10),
}

export function createProp(kind: PropKind, seed: number, tex: PropTextures): Object3D {
  const rng = new Rng(seed * 2654435761 + 17)
  switch (kind) {
    case 'crate':
      return makeCrate(rng, tex)
    case 'barrel':
      return makeBarrel(rng, tex)
    case 'tree':
      return makeTree(rng, tex)
    case 'car':
      return makeCar(rng, tex)
    case 'pillar':
      return makePillar(rng, tex)
    case 'barrier':
      return makeBarrier(rng, tex)
    case 'block':
      return makeBlock(rng, tex)
  }
}

function standard(options: {
  color: number
  map?: Texture
  roughnessMap?: Texture
  roughness?: number
  metalness?: number
}): MeshStandardMaterial {
  return new MeshStandardMaterial({
    color: options.color,
    map: options.map,
    roughnessMap: options.roughnessMap,
    roughness: options.roughness ?? 0.9,
    metalness: options.metalness ?? 0.05,
  })
}

function shade(base: number, rng: Rng, spread = 0.16): number {
  const c = new Color(base)
  const f = 1 + rng.spread(spread)
  c.multiplyScalar(f)
  return c.getHex()
}

function makeCrate(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const size = 1.05 + rng.spread(0.09)
  const mat = standard({
    color: shade(0x6b5a3c, rng),
    roughnessMap: tex.roughness,
    roughness: 0.95,
  })

  const box = new Mesh(GEO.crate, mat)
  box.scale.setScalar(size)
  box.position.y = (size * 1) / 2
  box.castShadow = true
  box.receiveShadow = true
  group.add(box)

  // Reinforcement planks: the detail that separates "box" from "crate".
  const plankMat = standard({ color: shade(0x4a3d28, rng), roughness: 0.95 })
  for (const y of [0.22, 0.8]) {
    for (const axis of [0, Math.PI / 2]) {
      const plank = new Mesh(GEO.plank, plankMat)
      plank.scale.setScalar(size)
      plank.position.y = y * size
      plank.rotation.y = axis
      plank.position.z = axis === 0 ? (size / 2) * 1.01 : 0
      plank.position.x = axis === 0 ? 0 : (size / 2) * 1.01
      group.add(plank)
    }
  }

  group.rotation.y = rng.angle()
  return group
}

function makeBarrel(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const mat = standard({
    color: shade(0x8a4a24, rng, 0.22),
    map: tex.rust,
    roughness: 0.75,
    metalness: 0.45,
  })

  const body = new Mesh(GEO.barrel, mat)
  body.position.y = 0.51
  body.castShadow = true
  body.receiveShadow = true
  group.add(body)

  const ringMat = standard({
    color: shade(0x5a3018, rng),
    roughness: 0.6,
    metalness: 0.6,
  })
  for (const y of [0.24, 0.78]) {
    const ring = new Mesh(GEO.barrelRing, ringMat)
    ring.position.y = y
    group.add(ring)
  }

  // Warning stripe so the explosive reads as dangerous at a glance.
  const stripe = new Mesh(GEO.barrelRing, standard({
    color: 0x9a1f10,
    roughness: 0.7,
    metalness: 0.2,
  }))
  stripe.scale.set(1.01, 2.4, 1.01)
  stripe.position.y = 0.51
  group.add(stripe)

  group.rotation.y = rng.angle()
  return group
}

function makeTree(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const height = 1 + rng.spread(0.22)
  const mat = standard({ color: 0x4a4636, map: tex.bark, roughness: 1 })

  const trunk = new Mesh(GEO.trunk, mat)
  trunk.scale.set(1, height, 1)
  trunk.position.y = 1.7 * height
  trunk.rotation.z = rng.spread(0.09)
  trunk.castShadow = true
  group.add(trunk)

  // Dead, broken branches — no foliage anywhere in the Zone.
  const branchCount = rng.int(3, 5)
  for (let i = 0; i < branchCount; i++) {
    const branch = new Mesh(GEO.branch, mat)
    const a = rng.angle()
    const y = 1.9 * height + rng.range(0, 1.3) * height
    branch.position.set(Math.sin(a) * 0.28, y, Math.cos(a) * 0.28)
    branch.rotation.set(rng.range(-0.5, 0.5), a, rng.range(0.6, 1.25))
    branch.scale.setScalar(rng.range(0.6, 1.15))
    branch.castShadow = true
    group.add(branch)
  }

  group.rotation.y = rng.angle()
  return group
}

function makeCar(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const paint = rng.pick([0x4a5140, 0x5a4636, 0x3d4650, 0x59503a])
  const mat = standard({
    color: shade(paint, rng, 0.2),
    map: tex.rust,
    roughness: 0.8,
    metalness: 0.4,
  })

  const body = new Mesh(GEO.carBody, mat)
  body.position.y = 0.78
  body.castShadow = true
  body.receiveShadow = true
  group.add(body)

  const cabin = new Mesh(GEO.carCabin, mat)
  cabin.position.set(-0.25 + rng.spread(0.2), 1.52, 0)
  // Collapsed roof: a wreck should never look like a parked car.
  cabin.rotation.z = rng.spread(0.12)
  cabin.scale.y = rng.range(0.6, 0.95)
  cabin.castShadow = true
  group.add(cabin)

  const wheelMat = standard({ color: 0x1a1a18, roughness: 1, metalness: 0 })
  const wheelPositions: Array<[number, number]> = [
    [-1.5, -0.92],
    [-1.5, 0.92],
    [1.5, -0.92],
    [1.5, 0.92],
  ]
  for (const [x, z] of wheelPositions) {
    if (rng.bool(0.22)) continue // a missing wheel or two
    const wheel = new Mesh(GEO.wheel, wheelMat)
    wheel.rotation.x = Math.PI / 2
    wheel.position.set(x, 0.36, z)
    wheel.castShadow = true
    group.add(wheel)
  }

  group.position.y = -0.12 // settled into the dirt
  return group
}

function makePillar(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const mat = standard({
    color: shade(0x53504a, rng),
    map: tex.concrete,
    roughnessMap: tex.roughness,
    roughness: 0.9,
  })

  const post = new Mesh(GEO.pillar, mat)
  post.position.y = 2.2
  post.rotation.z = rng.spread(0.07)
  post.castShadow = true
  group.add(post)

  const arm = new Mesh(GEO.pillarArm, mat)
  arm.position.y = 4.1
  arm.rotation.z = rng.spread(0.12)
  arm.castShadow = true
  group.add(arm)

  // Dead lamp head — the light died with everything else here.
  const lamp = new Mesh(
    GEO.lamp,
    standard({ color: 0x2c2c28, roughness: 0.6, metalness: 0.5 }),
  )
  lamp.position.set(rng.bool() ? 0.68 : -0.68, 3.98, 0)
  lamp.rotation.x = Math.PI
  group.add(lamp)

  group.rotation.y = rng.angle()
  return group
}

function makeBarrier(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const mat = standard({
    color: shade(0x7d4a28, rng, 0.24),
    map: tex.rust,
    roughness: 0.7,
    metalness: 0.5,
  })

  const rail = new Mesh(GEO.barrier, mat)
  rail.position.y = 0.52
  rail.rotation.z = rng.spread(0.05)
  rail.castShadow = true
  group.add(rail)

  for (const x of [-0.55, 0.55]) {
    const leg = new Mesh(GEO.barrierLeg, mat)
    leg.position.set(x, 0.25, 0)
    group.add(leg)
  }
  return group
}

function makeBlock(rng: Rng, tex: PropTextures): Object3D {
  const group = new Group()
  const mat = standard({
    color: shade(0x4e4c44, rng, 0.1),
    map: tex.concrete,
    roughnessMap: tex.roughness,
    roughness: 0.95,
  })

  const slab = new Mesh(GEO.block, mat)
  slab.position.y = 0.75
  slab.castShadow = true
  slab.receiveShadow = true
  group.add(slab)

  // A second, smaller slab tipped on top: broken structure, not a neat cube.
  const cap = new Mesh(GEO.block, mat)
  cap.scale.set(0.62, 0.5, 0.62)
  cap.position.set(rng.spread(0.4), 1.85, rng.spread(0.4))
  cap.rotation.y = rng.spread(0.5)
  cap.rotation.z = rng.spread(0.07)
  cap.castShadow = true
  group.add(cap)

  return group
}
